import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EditorDePlazos } from '@/components/EditorDePlazos';
import { normalizarPlazos, motivoParaNoGuardar as motivoPlazos, tienePlazos, tipoEfectivo } from '@/lib/renting';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Package, Edit, Trash2, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Eye, Search, Filter } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { confirmAsync } from '@/lib/confirm';

export default function Products({ user }) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  // Si este producto se ofrece en renting. Estado aparte del formulario
  // porque es una pregunta, no un campo: lo que se guarda es la tabla de
  // plazos, y desmarcar la casilla es mandarla a null.
  const [ofreceRenting, setOfreceRenting] = useState(false);
  
  // Import states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    ref: '',
    name: '',
    description: '',
    price: '',
    type: 'license',
    image: null,
    allow_custom_line: false,
    // '' = el del cliente. Solo se rellena para productos que van a un tipo
    // propio pase quien pase por caja (comisiones exentas, al 0%).
    tax_rate: '',
    tax_note: '',
    // Tabla de precios del renting. Vacia en todo lo demas.
    plazos: []
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, typeFilter, products]);

  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get('/products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const filterProducts = () => {
    let filtered = [...products];

    // Search filter (by name or ref)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(search) ||
        product.ref?.toLowerCase().includes(search)
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(product => tipoEfectivo(product) === typeFilter);
    }

    setFilteredProducts(filtered);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 2MB');
      return;
    }

    // Read and optimize image
    const reader = new FileReader();
    reader.onload = async (event) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas to resize
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 400;
        const MAX_HEIGHT = 400;
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64 with compression
        const optimizedBase64 = canvas.toDataURL('image/jpeg', 0.7);
        setFormData({ ...formData, image: optimizedBase64 });
        setImagePreview(optimizedBase64);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Un renting sin plazos, o con un plazo a medio teclear, no sirve
    // para nada: al anadirlo a un presupuesto no habria cuota que
    // elegir. Se para aqui, donde esta la errata, y no al presupuestar.
    if (ofreceRenting) {
      const pega = motivoPlazos(formData.plazos);
      if (pega) {
        toast.error(pega);
        return;
      }
    }

    setLoading(true);

    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        // Los productos dados de alta el 24-sep con tipo 'renting' se
        // pasan a equipo al guardarlos: van migrando solos.
        type: tipoEfectivo(formData),
        // '' es "el del cliente" y viaja como null: es lo que el servidor
        // entiende como "sin impuesto propio". Mandar '' daria 422.
        tax_rate: formData.tax_rate === '' || formData.tax_rate === null
          ? null : parseFloat(formData.tax_rate),
        tax_note: (formData.tax_note || '').trim() || null,
        // Desmarcar la casilla tiene que MANDAR null, no omitir el campo:
        // el servidor solo borra lo que viaja explicito, asi que omitirlo
        // dejaria los plazos vivos y el producto se seguiria alquilando.
        plazos: ofreceRenting ? normalizarPlazos(formData.plazos) : null,
      };

      if (editingProduct) {
        // Update existing product
        await axiosInstance.put(`/products/${editingProduct.id}`, productData);
        toast.success('Producto actualizado correctamente');
      } else {
        // Create new product
        await axiosInstance.post('/products', productData);
        toast.success('Producto creado correctamente');
      }

      setDialogOpen(false);
      setEditingProduct(null);
      setFormData({ ref: '', name: '', description: '', price: '', type: 'license', image: null, allow_custom_line: false, tax_rate: '', tax_note: '', plazos: [] });
    setOfreceRenting(false);
      setImagePreview(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setOfreceRenting(tienePlazos(product));
    setEditingProduct(product);
    setFormData({
      ref: product.ref || '',
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      // Los del 24-sep tienen tipo 'renting', que ya no esta en el
      // desplegable: se abren como Equipo y al guardar quedan migrados.
      // Sin esto el desplegable saldria en blanco.
      type: tipoEfectivo(product),
      image: product.image || null,
      allow_custom_line: !!product.allow_custom_line,
      tax_rate: product.tax_rate === null || product.tax_rate === undefined ? '' : String(product.tax_rate),
      tax_note: product.tax_note || '',
      plazos: product.plazos || []
    });
    setImagePreview(product.image || null);
    setDialogOpen(true);
  };

  const handleOpenCreateDialog = () => {
    setEditingProduct(null);
    setFormData({ ref: '', name: '', description: '', price: '', type: 'license', image: null, allow_custom_line: false, tax_rate: '', tax_note: '', plazos: [] });
    setOfreceRenting(false);
    setImagePreview(null);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirmAsync('¿Estás seguro de eliminar este producto?'))) return;

    try {
      await axiosInstance.delete(`/products/${id}`);
      toast.success('Producto eliminado');
      fetchProducts();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Por favor selecciona un archivo Excel (.xlsx o .xls)');
        return;
      }
      setImportFile(file);
      setPreviewData(null);
    }
  };

  const handlePreviewImport = async () => {
    if (!importFile) {
      toast.error('Selecciona un archivo Excel');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axiosInstance.post('/products/import-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPreviewData(response.data);
      
      if (response.data.total_errors > 0) {
        toast.warning(`Archivo procesado con ${response.data.total_errors} errores`);
      } else {
        toast.success('Vista previa generada correctamente');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar archivo');
    } finally {
      setImporting(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importFile) return;

    if (!(await confirmAsync(
      `⚠️ ATENCIÓN: Esta acción BORRARÁ los ${previewData.current_products_count} productos actuales ` +
      `e importará ${previewData.total_products} nuevos productos.\n\n¿Estás seguro de continuar?`
    ))) {
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axiosInstance.post('/products/import-confirm', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(
        `✓ Importación completada: ${response.data.imported_count} productos importados, ` +
        `${response.data.deleted_count} eliminados`
      );
      
      setImportDialogOpen(false);
      setImportFile(null);
      setPreviewData(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar productos');
    } finally {
      setImporting(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      license: 'Licencias',
      service: 'Servicios', 
      equipment: 'Equipos'
    };
    return labels[type] || type;
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      license: 'bg-blue-100 text-blue-800',
      service: 'bg-green-100 text-green-800',
      equipment: 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const groupKey = getTypeLabel(tipoEfectivo(product));
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(product);
    return acc;
  }, {});

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="products-container">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-[#212B35] mb-2" data-testid="products-title">Productos</h1>
            <p className="text-[#212B35]/70">Gestiona tu catálogo de productos</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setImportDialogOpen(true)}
              variant="outline"
              className="border-[#9C0F17] text-[#9C0F17] hover:bg-[#9C0F17] hover:text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importar Excel
            </Button>
            <Button 
              onClick={handleOpenCreateDialog}
              className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white" 
              data-testid="create-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Producto
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label>Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar por nombre o referencia..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div>
                <Label>Tipo de Producto</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    <SelectItem value="license">🔑 Licencias</SelectItem>
                    <SelectItem value="equipment">📦 Equipos</SelectItem>
                    <SelectItem value="service">🛠️ Servicios</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results count */}
        <div className="text-sm text-gray-600">
          {filteredProducts.length} {filteredProducts.length === 1 ? 'resultado' : 'resultados'}
          {searchTerm && ` para "${searchTerm}"`}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Editar Producto' : 'Crear Nuevo Producto'}</DialogTitle>
              <DialogDescription>
                {editingProduct ? 'Modifica' : 'Completa'} la información del producto
              </DialogDescription>
            </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ref">Referencia</Label>
                  <Input
                    id="ref"
                    data-testid="product-ref-input"
                    value={formData.ref}
                    onChange={(e) => setFormData({ ...formData, ref: e.target.value })}
                    placeholder="P001, SRV-001, etc. (opcional)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre</Label>
                  <Input
                    id="name"
                    data-testid="product-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    data-testid="product-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    placeholder={'Ej: Incluye:\n- Licencia Camarero10\n- Soporte {mes} {año}'}
                  />
                  {/* Angel, 21-sep-2026: "saltos de linea y que no sea todo
                      seguido, y variables como mes y año". Al entrar en una
                      factura, la linea se lleva el nombre y esto debajo, tal
                      cual, y el servidor resuelve las variables al emitir. */}
                  <p className="text-xs text-gray-500">
                    Sale en la factura debajo del nombre, con sus saltos de línea.
                    Admite <code className="bg-gray-100 px-1 rounded">{"{mes}"}</code> y <code className="bg-gray-100 px-1 rounded">{"{año}"}</code>,
                    que se rellenan al emitir cada factura.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Precio de venta (€)</Label>
                  <Input
                    id="price"
                    data-testid="product-price-input"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                  {/* A 0 no se ofrece comprarlo en el presupuesto: solo
                      se alquila. Ver `formasDePago` en lib/renting.js. */}
                  {ofreceRenting && Number(formData.price) === 0 && (
                    <p className="text-xs text-slate-500">
                      A 0 € solo se podrá ofrecer en renting, no en venta.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Tipo</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger data-testid="product-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="license">Licencias</SelectItem>
                      <SelectItem value="service">Servicios</SelectItem>
                      <SelectItem value="equipment">Equipos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* El renting es una forma de PAGO de cualquier producto,
                    no una categoria aparte (Angel, 25-sep-2026). Igual que
                    la linea a medida o el impuesto propio: una casilla. */}
                <div className="flex items-start gap-3 rounded-lg border p-3">
                  <input
                    id="ofrece_renting"
                    type="checkbox"
                    className="mt-1 h-4 w-4"
                    checked={ofreceRenting}
                    onChange={(e) => setOfreceRenting(e.target.checked)}
                    data-testid="ofrece-renting"
                  />
                  <div>
                    <Label htmlFor="ofrece_renting" className="cursor-pointer">
                      Se puede pagar a plazos (renting)
                    </Label>
                    <p className="text-xs text-slate-500">
                      En el presupuesto podrás elegir entre venderlo o
                      alquilarlo, sin salir de su sección.
                    </p>
                  </div>
                </div>

                {ofreceRenting && (
                  <EditorDePlazos
                    plazos={formData.plazos}
                    onChange={(plazos) => setFormData({ ...formData, plazos })}
                  />
                )}

                {/* Linea a medida: para productos cuyo alcance y precio se
                    pactan con cada cliente, como el desarrollo a medida. */}
                <div className="flex items-start gap-3 rounded-lg border p-3">
                  <input
                    id="allow_custom_line"
                    type="checkbox"
                    className="mt-1 h-4 w-4"
                    checked={!!formData.allow_custom_line}
                    onChange={(e) => setFormData({ ...formData, allow_custom_line: e.target.checked })}
                  />
                  <div>
                    <Label htmlFor="allow_custom_line" className="cursor-pointer">
                      Línea a medida
                    </Label>
                    <p className="text-xs text-gray-500">
                      En los presupuestos se podrán editar su descripción y su precio.
                      El precio de aquí se usa solo como punto de partida.
                    </p>
                  </div>
                </div>

                {/* Impuesto propio. Angel, 19-sep-2026: comisiones de venta de
                    un producto exento, al 0% aunque el cliente sea de Madrid.
                    Al añadirlo a una factura, la línea hereda esto y manda
                    sobre el impuesto del cliente. Lo único que no pisa es a un
                    cliente no sujeto: ahí todo va al 0. */}
                <div className="space-y-2 rounded-lg border p-3">
                  <Label htmlFor="tax_rate">Impuesto de este producto</Label>
                  <Select
                    value={formData.tax_rate === '' ? 'cliente' : String(formData.tax_rate)}
                    onValueChange={(v) => setFormData({
                      ...formData,
                      tax_rate: v === 'cliente' ? '' : v,
                      // Al volver a "el del cliente" la mención sobra.
                      tax_note: v === 'cliente' ? '' : formData.tax_note,
                    })}
                  >
                    <SelectTrigger data-testid="product-tax-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cliente">El del cliente (lo normal)</SelectItem>
                      <SelectItem value="0">0% — sin impuesto (exento / no sujeto)</SelectItem>
                      <SelectItem value="4">4% superreducido</SelectItem>
                      <SelectItem value="10">10% reducido</SelectItem>
                      <SelectItem value="21">21% general, siempre</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Manda sobre el impuesto del cliente en todas las facturas donde
                    entre este producto. Déjalo en "el del cliente" salvo que el
                    producto vaya a un tipo distinto pase quien pase por caja.
                  </p>

                  {formData.tax_rate === '0' && (
                    <div className="space-y-1 pt-1">
                      <Label htmlFor="tax_note">Mención que sale en la factura *</Label>
                      <Textarea
                        id="tax_note" rows={2}
                        value={formData.tax_note}
                        onChange={(e) => setFormData({ ...formData, tax_note: e.target.value })}
                        placeholder="Ej: Operación exenta de IVA (art. 20 Ley 37/1992)."
                        data-testid="product-tax-note"
                      />
                      <p className="text-xs text-gray-500">
                        Una línea sin impuesto tiene que decir por qué. Se imprime
                        debajo de los totales. Si no sabes el artículo, pregúntaselo
                        a la gestoría: es el mismo que llevaban estas facturas antes.
                      </p>
                    </div>
                  )}
                </div>

                {/* Image upload - only for equipment */}
                {formData.type === 'equipment' && (
                  <div className="space-y-2">
                    <Label htmlFor="image">Imagen del Equipo</Label>
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="cursor-pointer"
                    />
                    {imagePreview && (
                      <div className="mt-2 relative">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full h-40 object-contain border rounded-lg bg-gray-50"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute top-2 right-2"
                          onClick={() => {
                            setImagePreview(null);
                            setFormData({ ...formData, image: null });
                          }}
                        >
                          Eliminar
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      Máx. 2MB. Se optimizará automáticamente.
                    </p>
                  </div>
                )}

                <Button type="submit" className="w-full bg-[#9C0F17] hover:bg-[#7A0C12] text-white" disabled={loading} data-testid="product-submit-btn">
                  {loading ? (editingProduct ? 'Actualizando...' : 'Creando...') : (editingProduct ? 'Actualizar Producto' : 'Crear Producto')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

        {Object.keys(groupedProducts).length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              {searchTerm || typeFilter !== 'all' ? (
                <>
                  <p className="text-slate-600 mb-2">No se encontraron productos que coincidan con tu búsqueda</p>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchTerm('');
                      setTypeFilter('all');
                    }}
                  >
                    Limpiar filtros
                  </Button>
                </>
              ) : (
                <p className="text-slate-600">No hay productos aún. Crea el primero.</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedProducts).map(([typeLabel, items]) => (
              <Card key={typeLabel} className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl">{typeLabel}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((product) => (
                      <div
                        key={product.id}
                        className="p-4 border-2 border-slate-200 rounded-lg hover:border-blue-400 transition-colors"
                        data-testid={`product-card-${product.id}`}
                      >
                        {/* Image preview for equipment */}
                        {product.type === 'equipment' && product.image && (
                          <div className="mb-3">
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-full h-32 object-contain border rounded-lg bg-gray-50"
                            />
                          </div>
                        )}
                        
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            {product.ref && (
                              <span className="text-xs text-[#212B35]/50 font-mono">{product.ref}</span>
                            )}
                            <h3 className="font-semibold text-[#212B35]" data-testid="product-name">{product.name}</h3>
                          </div>
                          <Badge className={getTypeBadgeColor(tipoEfectivo(product))}>
                            {getTypeLabel(tipoEfectivo(product))}
                          </Badge>
                        </div>
                        {product.description && (
                          <p className="text-sm text-slate-600 mb-3 whitespace-pre-line">{product.description}</p>
                        )}
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-lg font-bold text-[#9C0F17]" data-testid="product-price">
                            {product.price > 0
                              ? `${product.price.toFixed(2)} €`
                              : (tienePlazos(product) ? 'Solo renting' : '0.00 €')}
                            {/* Que se puede alquilar, y por cuanto: desde la
                                lista, para no abrir la ficha a comprobarlo. */}
                            {tienePlazos(product) && (
                              <span
                                className="ml-2 text-xs font-medium text-amber-900 bg-amber-100 rounded px-1.5 py-0.5 align-middle"
                                title={normalizarPlazos(product.plazos)
                                  .map((p) => `${p.meses} meses: ${p.cuota.toFixed(2)} €/mes`)
                                  .join(" · ")}
                              >
                                renting
                              </span>
                            )}
                            {/* Se ve desde la lista: un producto al 0% que
                                pase desapercibido es un 21% que no se cobra. */}
                            {product.tax_rate !== null && product.tax_rate !== undefined && (
                              <span className="ml-2 text-xs font-medium text-slate-600 bg-slate-100 rounded px-1.5 py-0.5 align-middle"
                                    title="Impuesto propio: manda sobre el del cliente">
                                {product.tax_rate === 0 ? 'sin impuesto' : `${product.tax_rate}% fijo`}
                              </span>
                            )}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(product)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid="product-edit-btn"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(product.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid="product-delete-btn"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Import Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Importar Productos desde Excel
              </DialogTitle>
              <DialogDescription>
                Sube un archivo Excel con el formato especificado para importar productos
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Instructions */}
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="pt-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Formato del archivo Excel:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• <strong>Columna A:</strong> Código/Referencia del producto</li>
                    <li>• <strong>Columna B:</strong> Nombre del producto</li>
                    <li>• <strong>Columna C:</strong> Descripción (opcional)</li>
                    <li>• <strong>Columna D:</strong> Precio sin IVA</li>
                    <li>• <strong>Columna E:</strong> Tipo (LICENCIAS, SERVICIOS o EQUIPOS)</li>
                  </ul>
                  <p className="text-xs text-blue-700 mt-2">
                    ⚠️ Los datos comienzan directamente en la fila 1 (sin encabezados)
                  </p>
                </CardContent>
              </Card>

              {/* File Upload */}
              <div>
                <Label htmlFor="excel-file">Seleccionar archivo Excel</Label>
                <Input
                  id="excel-file"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportFileChange}
                  className="mt-2"
                />
                {importFile && (
                  <p className="text-sm text-gray-600 mt-1">
                    Archivo seleccionado: {importFile.name}
                  </p>
                )}
              </div>

              {/* Preview Button */}
              {importFile && !previewData && (
                <Button 
                  onClick={handlePreviewImport}
                  disabled={importing}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {importing ? 'Procesando...' : 'Generar Vista Previa'}
                </Button>
              )}

              {/* Preview Results */}
              {previewData && (
                <div className="space-y-4">
                  {/* Summary */}
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-gray-600">Productos a importar</p>
                          <p className="text-2xl font-bold text-green-700">{previewData.total_products}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Productos actuales</p>
                          <p className="text-2xl font-bold text-orange-700">{previewData.current_products_count}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Errores detectados</p>
                          <p className="text-2xl font-bold text-red-700">{previewData.total_errors}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Errors */}
                  {previewData.total_errors > 0 && (
                    <Card className="border-red-200 bg-red-50">
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Errores Encontrados ({previewData.total_errors})
                        </h4>
                        <ul className="text-sm text-red-800 space-y-1 max-h-40 overflow-y-auto">
                          {previewData.errors.map((error, index) => (
                            <li key={index}>• {error}</li>
                          ))}
                          {previewData.total_errors > 20 && (
                            <li className="font-semibold">... y {previewData.total_errors - 20} errores más</li>
                          )}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* Products Preview */}
                  <div>
                    <h4 className="font-semibold mb-2">Vista Previa (primeros 50 productos):</h4>
                    <div className="border rounded-lg max-h-80 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="p-2 text-left">Ref</th>
                            <th className="p-2 text-left">Nombre</th>
                            <th className="p-2 text-left">Tipo</th>
                            <th className="p-2 text-right">Precio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.products.map((product, index) => (
                            <tr key={index} className="border-t">
                              <td className="p-2 font-mono text-xs">{product.ref}</td>
                              <td className="p-2">{product.name}</td>
                              <td className="p-2">
                                <Badge className={
                                  product.type === 'license' ? 'bg-blue-100 text-blue-800' :
                                  product.type === 'service' ? 'bg-green-100 text-green-800' :
                                  'bg-purple-100 text-purple-800'
                                }>
                                  {product.type === 'license' ? 'Licencia' :
                                   product.type === 'service' ? 'Servicio' : 'Equipo'}
                                </Badge>
                              </td>
                              <td className="p-2 text-right font-semibold">{product.price.toFixed(2)}€</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Warning & Confirm */}
                  <Card className="border-orange-200 bg-orange-50">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-orange-900">⚠️ Atención</h4>
                          <p className="text-sm text-orange-800 mt-1">
                            Esta acción eliminará PERMANENTEMENTE los {previewData.current_products_count} productos 
                            actuales y los reemplazará con {previewData.total_products} nuevos productos del archivo Excel.
                          </p>
                          <p className="text-sm text-orange-800 mt-2 font-semibold">
                            Esta operación NO se puede deshacer.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Action Buttons */}
                  <div className="flex gap-2 justify-end pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setImportDialogOpen(false);
                        setImportFile(null);
                        setPreviewData(null);
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleConfirmImport}
                      disabled={importing || previewData.total_errors > 0}
                      className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {importing ? 'Importando...' : 'Confirmar Importación'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
